import {nanoid} from 'nanoid'
import {Tooltip as ReactTooltip} from 'react-tooltip'
import {forwardRef, useMemo} from 'react'

import type {ReactNode} from 'react'
import type {TooltipRefProps} from 'react-tooltip'

//  We need to use custom css to correctly angle the arrows, since tooltips might have
//  position:'top' but are still intelligently placed on the bottom if there is not enough
//  screen left above the component
import './tooltip.scss'

export interface TooltipBaseProps {
  children?: ReactNode
  delay?: number
  followCursor?: boolean
  anchorSelect?: string
  position?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end'
}

export interface TooltipProps extends TooltipBaseProps {
  title?: string
  //  styled text body content
  body?: string
  //  unstyled body content as react node
  bodyNode?: ReactNode
}

export const Tooltip = forwardRef<TooltipRefProps, TooltipProps>((props, ref) => {
  const {children, title, body, bodyNode, delay, position = 'top', followCursor, anchorSelect} = props
  const id = useMemo(() => `tooltip_${nanoid(10)}`, [])

  // We might have to change this solution for performance reason in the future
  // For now we will keep it for compatibility reasons
  // https://react-tooltip.com/docs/troubleshooting#dynamically-generated-anchor-elements
  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        // INFO: Tests will not fail if we remove tabIndex=0 from Tooltip, but this makes the tooltip host not focusable
        tabIndex={0}
        data-tooltip-id={id}
        aria-describedby={id}
      >
        {children}
      </a>
      <ReactTooltip
        id={id}
        ref={ref}
        delayShow={delay}
        float={followCursor}
        classNameArrow={'size-0 !bg-transparent border-x-spacing-6 border-x-transparent border-t-spacing-4 !border-t-interactive-surface-filled-default-default/80 backdrop-blur-2'}
        place={position}
        disableStyleInjection={true}
        anchorSelect={anchorSelect}
        opacity={1} // DO NOT CHANGE THIS - backdrop-blur won't work
        className={'z-10 flex min-w-0 max-w-spacing-260 flex-col gap-spacing-8 rounded-radius-md bg-interactive-surface-filled-default-default/80 px-spacing-32 py-spacing-28 backdrop-blur-2'}
      >
        {title && (
          <p className={'line-clamp-10 text-p1-emphasized text-glyph-inversed-primary'}>
            {title}
          </p>
        )}
        {body && (
          <p className={'line-clamp-10 text-p2-emphasized text-glyph-inversed-secondary/80'}>
            {body}
          </p>
        )}
        {bodyNode}
      </ReactTooltip>
    </>
  )
})
